import React, { useState, useRef, useEffect } from 'react';

const RgbColorSelector = ({ value, onChange, disabled = false }) => {
    const saturationRef = useRef(null);
    const hueRef = useRef(null);
    const [isDragging, setIsDragging] = useState(null);

    // Convert hex to HSV
    const hexToHsv = (hex) => {
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const diff = max - min;

        let h = 0;
        if (diff !== 0) {
            if (max === r) h = ((g - b) / diff) % 6;
            else if (max === g) h = (b - r) / diff + 2;
            else h = (r - g) / diff + 4;
        }
        h = Math.round(h * 60);
        if (h < 0) h += 360;

        const s = max === 0 ? 0 : diff / max;
        const v = max;

        return { h, s: s * 100, v: v * 100 };
    };

    // Convert HSV to hex
    const hsvToHex = (h, s, v) => {
        h /= 60;
        s /= 100;
        v /= 100;

        const c = v * s;
        const x = c * (1 - Math.abs((h % 2) - 1));
        const m = v - c;

        let r, g, b;
        if (h >= 0 && h < 1) [r, g, b] = [c, x, 0];
        else if (h >= 1 && h < 2) [r, g, b] = [x, c, 0];
        else if (h >= 2 && h < 3) [r, g, b] = [0, c, x];
        else if (h >= 3 && h < 4) [r, g, b] = [0, x, c];
        else if (h >= 4 && h < 5) [r, g, b] = [x, 0, c];
        else [r, g, b] = [c, 0, x];

        r = Math.round((r + m) * 255);
        g = Math.round((g + m) * 255);
        b = Math.round((b + m) * 255);

        const toHex = (n) => n.toString(16).padStart(2, '0');
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
    };

    const hsv = hexToHsv(value);

    const handleSaturationMouseDown = (e) => {
        if (disabled) return;
        setIsDragging('saturation');
        handleSaturationChange(e);
    };

    const handleHueMouseDown = (e) => {
        if (disabled) return;
        setIsDragging('hue');
        handleHueChange(e);
    };

    const handleSaturationChange = (e) => {
        const rect = saturationRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
        const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
        
        const s = (x / rect.width) * 100;
        const v = ((rect.height - y) / rect.height) * 100;
        
        const newHex = hsvToHex(hsv.h, s, v);
        onChange(newHex);
    };

    const handleHueChange = (e) => {
        const rect = hueRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
        const h = (x / rect.width) * 360;
        
        const newHex = hsvToHex(h, hsv.s, hsv.v);
        onChange(newHex);
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging === 'saturation') {
                handleSaturationChange(e);
            } else if (isDragging === 'hue') {
                handleHueChange(e);
            }
        };

        const handleMouseUp = () => {
            setIsDragging(null);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging, hsv]);

    return (
        <div className="rgb-color-selector">
            <div className="custom-color-picker">
                {/* Saturation/Value area */}
                <div 
                    ref={saturationRef}
                    className="saturation-area"
                    style={{ backgroundColor: `hsl(${hsv.h}, 100%, 50%)` }}
                    onMouseDown={handleSaturationMouseDown}
                >
                    <div className="saturation-overlay"></div>
                    <div className="value-overlay"></div>
                    <div 
                        className="saturation-pointer"
                        style={{
                            left: `${hsv.s}%`,
                            top: `${100 - hsv.v}%`
                        }}
                    ></div>
                </div>
                
                {/* Hue slider */}
                <div 
                    ref={hueRef}
                    className="hue-slider"
                    onMouseDown={handleHueMouseDown}
                >
                    <div 
                        className="hue-pointer"
                        style={{ left: `${(hsv.h / 360) * 100}%` }}
                    ></div>
                </div>
                
            </div>
        </div>
    );
};

export default RgbColorSelector;
